import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import { api, fileUrl } from "@/lib/api";
import { MapPin, Star, User } from "lucide-react";

export default function SelectedPlayers() {
  const [tehsils, setTehsils] = useState([]);
  const [selectedTehsil, setSelectedTehsil] = useState("");
  const [players, setPlayers] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/meta").then((r) => setTehsils(r.data.tehsils || []));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  useEffect(() => {
    if (!selectedTehsil) { setPlayers([]); return; }
    setLoading(true);
    api.get("/selected-players", { params: { tehsil: selectedTehsil } })
      .then((r) => setPlayers(r.data))
      .finally(() => setLoading(false));
  }, [selectedTehsil]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-20">
        <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Official Squad</div>
        <h1 className="font-display text-5xl md:text-7xl mt-3">Selected <span className="text-[#FFD700]">Players</span></h1>
        <p className="mt-3 text-white/70 max-w-2xl">Explore the officially selected NDPL squad by tehsil. Tap a tehsil to view every selected player from that region.</p>

        {/* Tehsil grid */}
        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-white/60 mb-4">Select Tehsil</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {tehsils.map((t) => (
              <button
                key={t}
                data-testid={`tehsil-${t}`}
                onClick={() => setSelectedTehsil(t)}
                className={`px-4 py-3 border rounded text-sm uppercase tracking-widest transition-all ${selectedTehsil === t ? "border-[#39FF14] bg-[#39FF14] text-black font-bold" : "border-white/15 bg-[#0B132B] text-white/80 hover:border-[#39FF14] hover:text-[#39FF14]"}`}
              >
                <MapPin size={14} className="inline mr-2 -mt-0.5" />{t}
              </button>
            ))}
          </div>
        </div>

        {/* Players list */}
        {selectedTehsil && (
          <div className="mt-12">
            <div className="flex items-center gap-3 mb-6">
              <Star className="text-[#FFD700]" />
              <h2 className="font-display text-3xl md:text-4xl">Selected from <span className="text-[#39FF14]">{selectedTehsil}</span></h2>
            </div>

            {loading ? (
              <div className="text-white/50">Loading…</div>
            ) : players.length === 0 ? (
              <div className="p-8 border border-dashed border-white/15 bg-[#0B132B] text-center text-white/60" data-testid="no-selected-players">No selected players from {selectedTehsil} yet.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="selected-players-grid">
                {players.map((p) => (
                  <div key={p.id} data-testid={`sp-${p.id}`} className="group relative overflow-hidden border border-white/10 bg-[#0B132B] hover:border-[#39FF14] transition-colors rounded-lg">
                    <div className="aspect-[3/4] bg-black/40 relative overflow-hidden">
                      {p.photo_file_id ? (
                        <img src={fileUrl(p.photo_file_id)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-white/30"><User size={48} /></div>
                      )}
                      <div className="absolute top-3 left-3 px-2 py-1 bg-[#39FF14] text-black text-[10px] uppercase tracking-widest font-bold">Selected</div>
                    </div>
                    <div className="p-4">
                      <div className="font-display text-2xl leading-tight">{p.name}</div>
                      <div className="mt-1 text-[#FFD700] text-xs uppercase tracking-widest">{p.role}</div>
                      <div className="mt-2 flex items-center gap-1 text-xs text-white/50"><MapPin size={12}/>{p.tehsil}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <Footer settings={settings} />
      <FloatingWhatsapp />
    </div>
  );
}
